<template>
  <div class="kb-rows">
    <!-- Bulk discount: what captains pay per pre-paid membership -->
    <div class="kb-row">
      <div class="min-w-0">
        <h4 class="kb-row-title">
          <span id="bulk-discount-mode-label">Bulk discount</span>
        </h4>
        <p class="kb-row-hint">
          {{ discountHint }}
        </p>
      </div>
      <div class="kb-row-body">
        <div class="kb-row-actions items-center">
          <SelectButton
            v-model="discountMode"
            :options="[...DISCOUNT_MODE_OPTIONS]"
            option-label="label"
            option-value="value"
            :allow-empty="false"
            :disabled="savingDiscount"
            size="small"
            aria-labelledby="bulk-discount-mode-label"
          />

          <InputNumber
            v-if="discountMode === 'percent'"
            v-model="percentValue"
            input-id="discount-percent"
            :min="0"
            :max="100"
            :step="0.5"
            mode="decimal"
            :max-fraction-digits="2"
            suffix=" %"
            show-buttons
            size="small"
            :input-style="{ width: '6rem' }"
            aria-label="Percent off"
            :disabled="savingDiscount"
            @blur="saveDiscount"
          />
          <InputNumber
            v-else-if="discountMode === 'fixed'"
            v-model="fixedPriceDollars"
            input-id="discount-fixed-price"
            :min="0"
            :max="10000"
            :step="1"
            mode="currency"
            currency="USD"
            locale="en-US"
            show-buttons
            size="small"
            :input-style="{ width: '7rem' }"
            aria-label="Fixed price"
            :disabled="savingDiscount"
            @blur="saveDiscount"
          />

          <Button
            label="Save"
            size="small"
            outlined
            severity="secondary"
            :loading="savingDiscount"
            @mousedown="commitPendingInput"
            @click="saveDiscount"
          />
        </div>
      </div>
      <div class="kb-row-aside">
        <span
          v-if="savedDiscountLabel"
          class="kb-tag kb-tag-neutral"
        >{{ savedDiscountLabel }}</span>
      </div>
    </div>

    <!-- Bulk discount cap, shared by both discount kinds -->
    <div class="kb-row">
      <div class="min-w-0">
        <h4
          id="bulk-discount-cap-label"
          class="kb-row-title"
        >
          Discount cap
        </h4>
        <p class="kb-row-hint">
          0 means no cap. When a cap is set, only that many discounted purchases will be allowed.
        </p>
        <p
          v-if="bulkFound && hasCap"
          class="kb-row-meta m-0 mt-1"
        >
          Used {{ bulkStatus?.discountUsed }} of {{ bulkStatus?.discountCap }} ({{ bulkStatus?.usedPct }}%)
        </p>
        <p
          v-else-if="!bulkFound && capValue > 0"
          class="kb-row-meta m-0 mt-1"
        >
          Cap configured: {{ capValue }} — will apply to the next bulk order
        </p>
        <p
          v-else
          class="kb-row-meta m-0 mt-1"
        >
          No cap set
        </p>
      </div>
      <div class="kb-row-aside">
        <InputNumber
          v-model="capValue"
          input-id="discount-cap"
          :min="0"
          :max="10000"
          :step="1"
          show-buttons
          size="small"
          :input-style="{ width: '4.5rem' }"
          aria-labelledby="bulk-discount-cap-label"
          :disabled="savingCap || isDiscountZero"
          @blur="saveCap"
        />
        <Button
          label="Save"
          size="small"
          outlined
          severity="secondary"
          :disabled="isDiscountZero"
          :loading="savingCap"
          @mousedown="commitPendingInput"
          @click="saveCap"
        />
      </div>
    </div>

    <!-- Comp tickets: free memberships minted into the pool -->
    <div class="kb-row">
      <div class="min-w-0">
        <h4
          id="bulk-mint-label"
          class="kb-row-title"
        >
          Comp tickets
        </h4>
        <p class="kb-row-hint">
          Free memberships added to the pool below, each with a code a member can redeem.
        </p>
      </div>
      <div class="kb-row-aside">
        <InputNumber
          v-model="mintCount"
          :min="1"
          :max="500"
          :step="1"
          show-buttons
          size="small"
          :input-style="{ width: '4rem' }"
          aria-labelledby="bulk-mint-label"
        />
        <Button
          :label="mintCount > 1 ? 'Add tickets' : 'Add ticket'"
          icon="pi pi-plus"
          size="small"
          outlined
          severity="secondary"
          :loading="minting"
          @click="mintCompTickets"
        />
      </div>
    </div>

    <!-- Pooled tickets: one line per ticket -->
    <section class="grid gap-1 pt-3 pb-1">
      <div class="flex flex-wrap items-baseline justify-between gap-x-3">
        <h3 class="kb-label m-0">
          Ticket pool · {{ seasonDisplay }}
        </h3>
        <KbStats
          v-if="tickets.length > 0"
          size="sm"
          :items="[
            { label: 'available', value: availableCount },
            { label: 'redeemed', value: redeemedCount, tone: redeemedCount > 0 ? 'ok' : undefined },
          ]"
        />
      </div>

      <ul
        v-if="tickets.length > 0"
        class="kb-tickets"
      >
        <li
          v-for="ticket in visibleTickets"
          :key="ticket.id"
          class="kb-ticket"
        >
          <span
            class="kb-tag"
            :class="ticketStatusTag(ticket).tagClass"
          >{{ ticketStatusTag(ticket).label }}</span>

          <div class="min-w-0">
            <p
              class="kb-ticket-code"
              :class="redeemedTextClass(ticket)"
            >
              {{ ticket.redeemCode }}
            </p>
            <p class="kb-row-meta m-0 flex flex-wrap items-center gap-x-1.5">
              <span v-if="ticket.source === 'bulk'">Bulk purchase</span>
              <span v-else-if="ticket.source === 'comp'">Admin comped</span>
              <template v-if="ticket.redeemedBy">
                <span>· redeemed by</span>
                <button
                  type="button"
                  class="kb-ticket-link"
                  @click="openMemberDrawer(ticket.redeemedBy)"
                >
                  {{ memberNameById(ticket.redeemedBy) }}
                </button>
              </template>
              <template v-if="ticket.stripeOrderId">
                <span v-tooltip.top="{ value: ticket.stripeOrderId, showDelay: 300 }">· Stripe …{{ String(ticket.stripeOrderId).slice(-8) }}</span>
                <button
                  v-tooltip.top="{ value: 'Copy Stripe order ID', showDelay: 300 }"
                  type="button"
                  class="kb-ticket-copy"
                  aria-label="Copy Stripe order ID"
                  @click="copyToClipboard(String(ticket.stripeOrderId))"
                >
                  <i class="pi pi-copy" />
                </button>
              </template>
            </p>
          </div>

          <span
            class="kb-ticket-amount"
            :class="priceTextClass(ticket)"
          >{{ formatPrice(ticket.price) }}</span>

          <span class="kb-ticket-action">
            <Button
              v-if="ticket.source === 'comp' && ticket.status === 'available'"
              v-tooltip.top="{ value: 'Remove ticket', showDelay: 300 }"
              icon="pi pi-trash"
              text
              severity="danger"
              size="small"
              aria-label="Remove ticket"
              :loading="removingId === ticket.id"
              @click="removeTicket(ticket.id)"
            />
          </span>
        </li>
      </ul>
      <p
        v-else
        class="kb-row-meta m-0"
      >
        No tickets in the pool yet
      </p>

      <Button
        v-if="tickets.length > visibleTickets.length"
        :label="`Show all ${tickets.length} tickets`"
        text
        severity="secondary"
        size="small"
        class="justify-self-start"
        @click="showAllTickets = true"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { markRaw, nextTick, onMounted, ref, watch, computed } from 'vue'
import { AdminMemberDrawerContent } from '#components'
import type { SubKrewe } from '~/shared/types/subkrewe'
import { useBulkDiscountStatus } from '~/composables/useBulkDiscountStatus.js'
import type { BulkDiscountType } from '~/shared/bulk/discount.js'
import { bulkDiscountLabel, formatCents, isBulkDiscountActive, readBulkDiscountTerms } from '~/shared/bulk/discount.js'
import type { PoolTicket } from '~/shared/types/pool.js'

interface Props {
  subkrewe: SubKrewe
}

const props = defineProps<Props>()
const emit = defineEmits(['update'])
const toast = useToast()
const { paradeYear } = useParadeYear()
const year = computed(() => String(paradeYear.value))
const seasonDisplay = computed(() => `${paradeYear.value - 1}-${paradeYear.value}`)

// Discount edit state. 'none' is a UI-only mode: it persists as a 'percent'
// discount of 0, which is what every consumer already treats as no discount.
type DiscountMode = 'none' | BulkDiscountType

const DISCOUNT_MODE_OPTIONS: ReadonlyArray<{ label: string, value: DiscountMode }> = [
  { label: 'None', value: 'none' },
  { label: 'Percent off', value: 'percent' },
  { label: 'Fixed price', value: 'fixed' },
]

function modeFor(subkrewe: SubKrewe): DiscountMode {
  const terms = readBulkDiscountTerms(subkrewe)
  return isBulkDiscountActive(terms) ? terms.type : 'none'
}

const discountMode = ref<DiscountMode>(modeFor(props.subkrewe))
const percentValue = ref<number>(props.subkrewe.discount_percent ?? 0)
// Stored in cents; edited in dollars
const fixedPriceDollars = ref<number>((props.subkrewe.discount_fixed_price ?? 0) / 100)
const savingDiscount = ref(false)
const subkrewesStore = useSubKrewesStore()

watch(
  () => [
    props.subkrewe.discount_type,
    props.subkrewe.discount_percent,
    props.subkrewe.discount_fixed_price,
  ] as const,
  () => {
    discountMode.value = modeFor(props.subkrewe)
    percentValue.value = props.subkrewe.discount_percent ?? 0
    fixedPriceDollars.value = (props.subkrewe.discount_fixed_price ?? 0) / 100
  },
)

// The saved discount, shown beside the row title ("15% off", "$40.00 each")
const savedDiscountLabel = computed(() => bulkDiscountLabel(readBulkDiscountTerms(props.subkrewe)))

const discountHint = computed(() => {
  if (discountMode.value === 'fixed') {
    const price = formatCents(Math.max(0, Math.round(Number(fixedPriceDollars.value ?? 0) * 100)))
    return `Captains pay ${price} per membership regardless of the current pricing tier. `
      + 'Use this to honour a promised price after tiers have moved up. A fixed price above the '
      + 'current tier price is ignored, so captains never pay more than the tier price. '
      + '$0 means no discount — mint comp tickets below to give memberships away.'
  }
  if (discountMode.value === 'percent') {
    return 'Takes this percentage off whatever the current pricing tier costs. 0 means no discount.'
  }
  return 'No bulk discount — captains pay the current tier price.'
})

async function saveDiscount() {
  if (savingDiscount.value) return
  savingDiscount.value = true
  try {
    // Ensure any pending v-model updates from InputNumber are flushed (after blur on mousedown)
    await nextTick()
    const mode = discountMode.value
    const percent = mode === 'percent'
      ? Math.max(0, Math.min(100, Number(percentValue.value ?? 0)))
      : 0
    const fixedPrice = mode === 'fixed'
      ? Math.max(0, Math.round(Number(fixedPriceDollars.value ?? 0) * 100))
      : 0

    const updates: Partial<SubKrewe> = {
      discount_type: mode === 'fixed' ? 'fixed' : 'percent',
      discount_percent: percent,
      discount_fixed_price: fixedPrice,
    }

    // Both modes share the cap, so clearing the discount clears the cap too
    if (percent === 0 && fixedPrice === 0) {
      capValue.value = 0
      updates.discount_cap = 0
    }

    const ok = await subkrewesStore.updateSubKrewe(props.subkrewe, updates)
    if (ok) {
      emit('update', updates)
      toast.add({ severity: 'success', summary: 'Saved', detail: 'Discount updated', life: 2000 })
    }
    else {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to save discount', life: 3000 })
    }
  }
  catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: String(e), life: 3000 })
  }
  finally {
    savingDiscount.value = false
  }
}

function commitPendingInput() {
  // Ensure any focused input (like InputNumber) blurs to commit its internal value
  const ae = document.activeElement as HTMLElement | null
  if (ae && typeof ae.blur === 'function') ae.blur()
}

// Pool tickets live list
const { tickets } = usePoolTickets(props.subkrewe.id, year)

// The list shows the first few tickets until asked for the rest
const TICKETS_SHOWN = 10
const showAllTickets = ref(false)
const visibleTickets = computed(() => showAllTickets.value ? tickets.value : tickets.value.slice(0, TICKETS_SHOWN))
const availableCount = computed(() => tickets.value.filter(t => t.status === 'available').length)
const redeemedCount = computed(() => tickets.value.filter(t => t.status === 'redeemed').length)

// Phosphor tag for a ticket: an available one can still be handed out (OK);
// a redeemed, refunded or expired one is spent (neutral)
const TICKET_STATUS_LABELS: Record<PoolTicket['status'], string> = {
  available: 'Available',
  redeemed: 'Redeemed',
  refunded: 'Refunded',
  expired: 'Expired',
}
function ticketStatusTag(ticket: PoolTicket): { label: string, tagClass: string } {
  return {
    label: TICKET_STATUS_LABELS[ticket.status] ?? ticket.status,
    tagClass: ticket.status === 'available' ? 'kb-tag-ok' : 'kb-tag-neutral',
  }
}

// Mint comp tickets
const mintCount = ref(1)
const minting = ref(false)
async function mintCompTickets() {
  if (minting.value) return
  minting.value = true
  try {
    const res = await $fetch('/api/admin/pool/mint', {
      method: 'POST',
      body: { subkreweId: props.subkrewe.id, count: Number(mintCount.value) || 1, type: 'membership', year: year.value },
    }) as { success: boolean, tickets?: Array<{ ticketId: string, redeemCode: string }>, message?: string }
    if (!res.success) throw new Error(res.message || 'Mint failed')
    toast.add({ severity: 'success', summary: 'Minted', detail: `${res.tickets?.length || 0} comp tickets created`, life: 2500 })
  }
  catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: String(e), life: 3000 })
  }
  finally {
    minting.value = false
  }
}

// Remove available comp ticket
const removingId = ref<string | null>(null)
async function removeTicket(ticketId: string) {
  if (removingId.value) return
  removingId.value = ticketId
  try {
    const res = await $fetch('/api/admin/pool/delete', {
      method: 'POST',
      body: { subkreweId: props.subkrewe.id, ticketId, year: year.value },
    }) as { success: boolean, message?: string }
    if (!res.success) throw new Error(res.message || 'Remove failed')
    toast.add({ severity: 'success', summary: 'Removed', detail: 'Ticket removed', life: 2000 })
  }
  catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: String(e), life: 3000 })
  }
  finally {
    removingId.value = null
  }
}

// Member name + drawer link, aligned with MembersSection
const membersStore = useMembersStore()
const { setInternalNavigation, openDrawer } = useDrawer()
const memberNameById = (uid?: string): string => {
  if (!uid) return '—'
  const m = membersStore.members[`members/${uid}`]
  return m?.name || uid
}
async function openMemberDrawer(uid?: string) {
  if (!uid) return
  // Ensure member is in store
  if (!membersStore.members[`members/${uid}`]) {
    try {
      await membersStore.fetchMember(uid)
    }
    catch {
      // ignore
    }
  }
  const member = membersStore.members[`members/${uid}`]
  if (!member) return
  setInternalNavigation(true)
  openDrawer({
    title: member.name || 'Member Details',
    data: member,
    component: markRaw(AdminMemberDrawerContent),
  })
}

// Lighten text for redeemed rows
function redeemedTextClass(t: { status?: string }): string | undefined {
  return t?.status === 'redeemed' ? 'text-kb-dim' : undefined
}

// A missing price ("—") is dim like a redeemed ticket's
function priceTextClass(t: PoolTicket): string | undefined {
  return formatPrice(t.price) === '—' ? 'text-kb-dim' : redeemedTextClass(t)
}

// Format a ticket price. Supports both shapes:
// - Stripe webhook: { total: number (minor units), currency: string }
// - Legacy Eventbrite: { grossDisplay: string }
function formatPrice(price?: { total?: number | null, currency?: string | null, grossDisplay?: string | null }): string {
  if (!price) return '—'
  if (typeof price.total === 'number' && price.total > 0) {
    const currency = (price.currency || 'usd').toUpperCase()
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price.total / 100)
    }
    catch {
      return `${(price.total / 100).toFixed(2)} ${currency}`
    }
  }
  return price.grossDisplay ?? '—'
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard?.writeText?.(text)
  }
  catch {
    // no-op
  }
}

// Bulk discount cap state & actions
const subkreweIdRef = ref<string | null>(props.subkrewe.id)
const { status: bulkStatusRef, found: bulkFoundRef, hasCap, refresh: refreshBulk } = useBulkDiscountStatus(subkreweIdRef)
const bulkStatus = computed(() => bulkStatusRef.value)
const bulkFound = computed(() => bulkFoundRef.value)
const isDiscountZero = computed(() => {
  if (discountMode.value === 'percent') return Number(percentValue.value ?? 0) <= 0
  if (discountMode.value === 'fixed') return Number(fixedPriceDollars.value ?? 0) <= 0
  return true
})
// Cap configured at the SubKrewe level; applied to next code creation
const capValue = ref<number>(props.subkrewe.discount_cap ?? 0)
const savingCap = ref(false)

// Keep input reflecting SubKrewe-level configured cap
watch(() => props.subkrewe.discount_cap, (v) => {
  const n = typeof v === 'number' ? v : 0
  if (n !== capValue.value) capValue.value = n
}, { immediate: true })

onMounted(() => {
  refreshBulk()
})

// If the discount becomes 0 in either mode, force local cap to 0 for UX clarity
watch(isDiscountZero, (zero) => {
  if (zero && capValue.value !== 0) capValue.value = 0
})

async function saveCap() {
  if (savingCap.value) return
  savingCap.value = true
  try {
    await nextTick()
    const newCap = Math.max(0, Math.floor(Number(capValue.value ?? 0)))
    // Persist the configured cap on the SubKrewe; applied when creating the next Eventbrite access code
    const ok = await subkrewesStore.updateSubKrewe(props.subkrewe, { discount_cap: newCap })
    if (ok) {
      emit('update', { discount_cap: newCap })
      toast.add({ severity: 'success', summary: 'Saved', detail: 'Cap updated', life: 2000 })
      // Optionally refresh current usage if a code exists
      if (bulkFound.value) await refreshBulk()
    }
    else {
      throw new Error('Failed to update cap')
    }
  }
  catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: String(e), life: 3000 })
  }
  finally {
    savingCap.value = false
  }
}
</script>

<style scoped>
.kb-tickets {
  margin: 0;
  padding: 0;
  list-style: none;
}

.kb-ticket {
  display: grid;
  grid-template-columns: 5.5rem minmax(0, 1fr) auto 1.75rem;
  align-items: baseline;
  gap: 0.125rem 0.75rem;
  padding: 0.5rem 0;
}

.kb-ticket + .kb-ticket {
  border-top: 1px solid var(--kb-line);
}

.kb-ticket .kb-tag {
  justify-self: start;
}

.kb-ticket-code {
  margin: 0;
  font: 13px/1.45 var(--kb-font-mono);
  color: var(--kb-fg);
  overflow-wrap: anywhere;
}

.kb-ticket-amount {
  font: 13px var(--kb-font-mono);
  font-variant-numeric: tabular-nums;
  color: var(--kb-fg);
}

.kb-ticket-action {
  align-self: center;
  display: flex;
  justify-content: flex-end;
}

.kb-ticket-action .p-button-sm {
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
}

.kb-ticket-action .p-button-sm :deep(.p-button-icon) {
  font-size: 0.75rem;
}

.kb-ticket-link,
.kb-ticket-copy {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: var(--kb-muted);
  cursor: pointer;
}

.kb-ticket-link:hover,
.kb-ticket-copy:hover {
  color: var(--kb-accent);
}

.kb-ticket-link:hover {
  text-decoration: underline;
}

.kb-ticket-copy {
  padding: 0 0.125rem;
  color: var(--kb-dim);
}

.kb-ticket-copy .pi {
  font-size: 11px;
}

.kb-ticket-link:focus-visible,
.kb-ticket-copy:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 1px;
  border-radius: 2px;
}
</style>
